import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';

import { Booking } from '../models';
import { BookingService } from './booking.service';
import { ErrorHandlerService } from './error-handler.service';
import { Injectable } from '@angular/core';
import { List } from 'immutable';
import { Observable, BehaviorSubject } from 'rxjs';
import { SalonService } from './salon.service';
import { SalonmonsterHttpClient } from '../services/salonmonster-http-client';
import { ConnectionService } from './connection.service';
import { FEATURE_FLAGS, PostHogService } from './posthog.service';
import { StylistService } from './stylist.service';
import { UserService } from './user.service';
import { Utilities } from '../utilities/utilities';
import { switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class BackgroundProcessService extends SalonmonsterHttpClient {
  private bookingRequestMonitor = new BehaviorSubject<number>(0);

  private bookingWaitListMonitor = new BehaviorSubject<number>(0);

  private pendingConnectionRequestMonitor = new BehaviorSubject<number>(0);

  public readonly bookingRequestMonitor$ = this.bookingRequestMonitor.asObservable();

  public readonly bookingWaitListMonitor$ = this.bookingWaitListMonitor.asObservable();

  /** Connection requests waiting on this stylist to answer. Drives the side-menu badge. */
  public readonly pendingConnectionRequestMonitor$ =
    this.pendingConnectionRequestMonitor.asObservable();

  public init: boolean;

  private _muteRefreshSession: boolean;

  private bookingRequestCount = 0;

  private bookingWaitListCount = 0;

  /**
   * Bumped by every pending-request refresh and by logout, and compared
   * against lastPublishedPendingToken to decide whether a response may publish.
   *
   * Several triggers can have a refresh in flight at once (login, the interval,
   * the change signal, and a late feature-flag resolution), so without this an
   * older response can overwrite a newer mutation-driven count, or one started
   * before logout can repopulate the badge after the reset. Mirrors the
   * generation token in StylistPickerComponent.refresh().
   */
  private pendingConnectionRequestToken = 0;

  /**
   * The token of the newest response that actually published.
   *
   * Supersession is keyed on this rather than on the newest refresh *started*,
   * because a newer refresh that fails publishes nothing — and gating on the
   * start token would let that failure discard an older response that did
   * succeed, leaving the badge stale until the next trigger.
   */
  private lastPublishedPendingToken = 0;

  constructor(
    http: HttpClient,
    public userService: UserService,
    private stylistService: StylistService,
    private salonService: SalonService,
    private bookingService: BookingService,
    private connectionService: ConnectionService,
    private postHogService: PostHogService,
    protected errorHandlerService: ErrorHandlerService
  ) {
    super(http, userService, errorHandlerService);

    this.userService.getLogInMonitor().subscribe((login) => {
      if (!this.init) {
        this.init = true;
        return;
      }

      if (!login) {
        return true;
      }

      this.refreshSession().subscribe(() => {});

      this.refreshBookingRequest();
      this.refreshBookingWaitlist();
      this.refreshPendingConnectionRequests();
    });

    this.userService.getLogOutMonitor().subscribe((logout) => {
      if (logout) {
        this.bookingRequestMonitor.next(0);
        this.bookingWaitListMonitor.next(0);
        // Close the window first: every refresh already in flight must be
        // unable to land after this reset and repopulate a logged-out badge.
        this.pendingConnectionRequestToken++;
        this.lastPublishedPendingToken = this.pendingConnectionRequestToken;
        this.pendingConnectionRequestMonitor.next(0);
      }
    });

    // A mutation the user just performed should move the badge now rather than
    // waiting up to 30 minutes for the next cycle.
    this.connectionService.connectionRequestsChanged$.subscribe(() => {
      this.refreshPendingConnectionRequests();
    });

    // isFeatureEnabled() reads false until PostHog resolves, so the refresh in
    // startScript() can run before the flag is known. Re-run when flags land,
    // the same shape AppComponent uses to resolve its flagged menu entries.
    this.postHogService.onFeatureFlags(() => {
      this.refreshPendingConnectionRequests();
    });

    this.startScript();
  }

  private startScript() {
    // refresh  session every 15 mins
    this.refreshSession().subscribe(() => {});
    setInterval(() => {
      this.refreshSession().subscribe(() => {});
    }, 1000 * (60 * 15));
    // }, 1000 * (5));

    // refresh every 30 mins
    this.refreshBookingRequest();
    this.refreshBookingWaitlist();
    // No boot call for the pending-request count: isFeatureEnabled() reads
    // false until PostHog resolves, so this would either no-op or duplicate
    // the onFeatureFlags callback that fires synchronously on a warm start.
    setInterval(() => {
      this.refreshBookingRequest();
      this.refreshBookingWaitlist();
      this.refreshPendingConnectionRequests();
    }, 1000 * (60 * 30));
  }

  public refreshSession(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      if (this._muteRefreshSession || !this.userService.isLoggedIn()) {
        observer.next(false);
        observer.complete();
        return;
      }

      if (this.userService.isLoggedIn()) {
        // Remember whose session this refresh belongs to: an admin
        // impersonation can replace the session while the requests are in
        // flight, and a stale response must not overwrite the new user.
        const sessionUserID = this.userService.getUser().getID();
        const abortIfSessionReplaced = (): boolean => {
          const current = this.userService.getUser();
          if (current && current.getID() === sessionUserID) {
            return false;
          }
          observer.next(false);
          observer.complete();
          return true;
        };
        this.stylistService
          .loadStylistInfo(sessionUserID)
          .subscribe({
            next: (stylist) => {
              if (abortIfSessionReplaced()) {
                return;
              }
              this.userService.setUser(stylist);

              // check if this user is still acive
              if (!this.userService.getUser().isActive()) {
                this.userService.logout();
                return;
              }

              this.salonService.load().subscribe({
                next: (salon) => {
                  if (abortIfSessionReplaced()) {
                    return;
                  }
                  this.userService.setSalon(salon);
                  this.userService.sessionMonitor.next({
                    user: this.userService.getUser(),
                    salon: this.userService.getSalon(),
                    token: this.userService.getToken()
                  });

                  observer.next(true);
                  observer.complete();
                },
                error: (err) => {
                  if (err.status === 401 || err.status === 404) {
                    this.userService.logout();
                    return;
                  }
                  observer.next(false);
                  observer.complete();
                }}
              );
            },
            error: (err) => {
              if (err.status === 401 || err.status === 404) {
                this.userService.logout();
                return;
              }

              observer.next(false);
              observer.complete();
            }}
          );
      } else {
        // observer.error(new Error('User is not logged in'));
        // observer.complete();
        observer.next(false);
        observer.complete();
      }
    });
  }

  public refreshBookingRequest() {
    if (!this.userService.isLoggedIn()) {
      return;
    }

    this.bookingService
      .loadBookingRequestCount(this.userService.getUser().getID())
      .subscribe(
        (count) => {
          this.bookingRequestCount = count;
          this.bookingRequestMonitor.next(this.bookingRequestCount);
        }
      );
  }

  public refreshBookingWaitlist() {
    if (!this.userService.isLoggedIn()) {
      return;
    }

    this.bookingService
      .loadWaitListBookingsCount(this.userService.getUser().getID())
      .subscribe(
        (count) => {
          this.bookingWaitListCount = count;
          this.bookingWaitListMonitor.next(this.bookingWaitListCount);
        }
      );
  }

  /**
   * Publishes how many connection requests are waiting on this stylist.
   *
   * Gated on the feature flag so salons without connectable accounts never
   * issue the request at all — the menu entry is hidden for them anyway.
   */
  public refreshPendingConnectionRequests() {
    if (!this.userService.isLoggedIn()) {
      return;
    }

    if (!this.postHogService.isFeatureEnabled(FEATURE_FLAGS.connectableAccounts)) {
      return;
    }

    const token = ++this.pendingConnectionRequestToken;
    const stylistID = this.userService.getUser().getID();

    this.connectionService.getConnectionRequests().subscribe({
      next: (connectionRequests) => {
        // A newer response already published, or a logout closed the window.
        if (token <= this.lastPublishedPendingToken) {
          return;
        }

        // The account changed under this request — count it against nobody.
        if (stylistID !== this.userService.getUser()?.getID()) {
          return;
        }

        this.lastPublishedPendingToken = token;
        this.pendingConnectionRequestMonitor.next(
          connectionRequests.filter((request) => request.isPendingFor(stylistID)).length
        );
      },
      error: (err) => {
        // Hold the last known count rather than zeroing the badge on a blip.
        // The trade-off: a failed refetch right after an accept leaves the
        // handled request counted until the next successful trigger.
        console.error('[background-process] failed to refresh pending connection requests', err);
      },
    });
  }

  public muteRefreshSession() {
    this._muteRefreshSession = true;
  }

  public unMuteRefreshSession() {
    this._muteRefreshSession = false;
  }

  public updateBookingRequestCount(delta: number) {
    this.bookingRequestCount += delta;
    this.bookingRequestCount =
      this.bookingRequestCount >= 0 ? this.bookingRequestCount : 0;
    this.bookingRequestMonitor.next(this.bookingRequestCount);
  }

  public updateBookingWaitListCount(delta: number) {
    this.bookingWaitListCount += delta;
    this.bookingWaitListCount =
      this.bookingWaitListCount >= 0 ? this.bookingWaitListCount : 0;
    this.bookingWaitListMonitor.next(this.bookingWaitListCount);
  }
}
